import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "bootstrap-icons/font/bootstrap-icons.css";
import { prefetchPublicData } from "../utils/publicDataCache";
import { API_BASE } from "../utils/apiBase";
import { policyPages as defaultPolicyPages } from "../Pages/PolicyPages";

function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 991);
  const [policyMenuItems, setPolicyMenuItems] = useState([]);
  const fallbackPolicyItems = defaultPolicyPages.map((item) => ({
    label: item.navLabel || item.title || "Policy",
    path: item.path,
  }));
  const navRef = useRef(null);

  useEffect(() => {
    setMobileOpen(false);
    setOpenDropdown(null);
  }, [location.pathname]);

  useEffect(() => {
    const handleResize = () => {
      const nextIsMobile = window.innerWidth <= 991;
      setIsMobile(nextIsMobile);
      if (!nextIsMobile) {
        setMobileOpen(false);
        setOpenDropdown(null);
      }
    };

    const handleOutsideClick = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setMobileOpen(false);
        setOpenDropdown(null);
      }
    };

    window.addEventListener("resize", handleResize);
    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      window.removeEventListener("resize", handleResize);
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    let active = true;
    fetch(`${API_BASE}/policy-pages/public`)
      .then((res) => (res.ok ? res.json() : []))
      .then((rows) => {
        if (!active || !Array.isArray(rows)) return;
        const mapped = rows
          .filter((row) => row?.path && row?.navLabel && row?.isVisible !== false)
          .map((row) => ({ label: row.navLabel, path: row.path }));
        setPolicyMenuItems(mapped.length ? mapped : fallbackPolicyItems);
      })
      .catch(() => {
        if (!active) return;
        setPolicyMenuItems(fallbackPolicyItems);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!isMobile) {
      document.body.style.overflow = "";
      return undefined;
    }

    document.body.style.overflow = mobileOpen ? "hidden" : "";

    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobile, mobileOpen]);

  useEffect(() => {
    prefetchPublicData("public-states", "/states");
    prefetchPublicData("public-course-catalog", "/courses-public");
    prefetchPublicData("public-gallery-occasions", "/gallery-occasions");
    prefetchPublicData("public-download-sections", "/download-sections");
    prefetchPublicData("public-districts", "/districts");
    prefetchPublicData("public-approved-franchises", "/approved-franchises");
  }, []);

  const menuGroups = [
    { label: "Home", path: "/" },
    {
      label: "About Us",
      children: [{ label: "Our Story", path: "/our-story" }],
    },
    {
      label: "Our Policy",
      children: policyMenuItems,
    },
    {
      label: "Franchise",
      children: [
        { label: "Study Centre", path: "/our-centre" },
        { label: "Centre Login", path: "/franchise-login" },
        { label: "Application", path: "/franchise-apply" },
        { label: "Application Status", path: "/franchise-application-status" },
      ],
    },
    {
      label: "Student Panel",
      children: [
        { label: "Courses", path: "/our-courses" },
        { label: "Registration", path: "/student-registration" },
        { label: "Student Verification", path: "/student-verification" },
        { label: "Result Verification", path: "/result-verification" },
        { label: "Student Login", path: "/student-login" },
      ],
    },
    { label: "Download", path: "/downloadcenter" },
    { label: "Gallery", path: "/gallery" },
    { label: "Contact Us", path: "/contact" },
  ];

  const isActive = (item) => {
    if (item.path) return location.pathname === item.path;
    return item.children?.some((child) => location.pathname === child.path);
  };

  return (
    <nav className="site-navbar" ref={navRef}>
      {mobileOpen ? <div className="site-navbar__backdrop" onClick={() => setMobileOpen(false)} /> : null}
      <div className="site-navbar__inner">
        <button
          type="button"
          className={`site-navbar__toggle ${mobileOpen ? "is-open" : ""}`}
          onClick={() => setMobileOpen((prev) => !prev)}
          aria-label="Toggle navigation"
        >
          <i className={`bi ${mobileOpen ? "bi-x-lg" : "bi-list"}`}></i>
        </button>

        <div className="site-navbar__menu">
          {menuGroups.map((item) => (
            <div
              key={item.label}
              className={`site-navbar__item ${item.children ? "has-dropdown" : ""} ${isActive(item) ? "is-active" : ""}`}
              onMouseEnter={() => !isMobile && item.children && setOpenDropdown(item.label)}
              onMouseLeave={() => !isMobile && item.children && setOpenDropdown(null)}
            >
              {item.children ? (
                <>
                  <button
                    type="button"
                    className="site-navbar__link"
                    onClick={() => {
                      if (isMobile) {
                        setOpenDropdown((prev) => (prev === item.label ? null : item.label));
                      }
                    }}
                  >
                    <span>{item.label}</span>
                    <i className={`bi ${openDropdown === item.label ? "bi-chevron-up" : "bi-chevron-down"}`}></i>
                  </button>

                  <div
                    className={`site-navbar__dropdown ${item.label === "Our Policy" ? "site-navbar__dropdown--policy" : ""} ${openDropdown === item.label ? "is-open" : ""}`}
                  >
                    {item.children.map((child) => (
                      <button
                        key={child.path}
                        type="button"
                        className={`site-navbar__dropdown-link ${item.label === "Our Policy" ? "site-navbar__dropdown-link--policy" : ""} ${location.pathname === child.path ? "is-active" : ""}`}
                        onClick={() => navigate(child.path)}
                      >
                        {child.label}
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <button type="button" className="site-navbar__link" onClick={() => navigate(item.path)}>
                  <span>{item.label}</span>
                </button>
              )}
            </div>
          ))}
        </div>

        <div className={`site-navbar__drawer ${mobileOpen ? "is-open" : ""}`}>
          <div className="site-navbar__drawer-head">
            <strong>Menu</strong>
            <button type="button" className="site-navbar__drawer-close" onClick={() => setMobileOpen(false)} aria-label="Close navigation">
              <i className="bi bi-x-lg"></i>
            </button>
          </div>

          <div className="site-navbar__drawer-body">
            {menuGroups.map((item) => (
              <div key={`mobile-${item.label}`} className={`site-navbar__drawer-item ${isActive(item) ? "is-active" : ""}`}>
                {item.children ? (
                  <>
                    <button
                      type="button"
                      className="site-navbar__drawer-link"
                      onClick={() => setOpenDropdown((prev) => (prev === item.label ? null : item.label))}
                    >
                      <span>{item.label}</span>
                      <i className={`bi ${openDropdown === item.label ? "bi-chevron-up" : "bi-chevron-down"}`}></i>
                    </button>

                    <div className={`site-navbar__drawer-submenu ${openDropdown === item.label ? "is-open" : ""}`}>
                      {item.children.map((child) => (
                        <button
                          key={`mobile-${child.path}`}
                          type="button"
                          className={`site-navbar__drawer-sublink ${location.pathname === child.path ? "is-active" : ""}`}
                          onClick={() => navigate(child.path)}
                        >
                          {child.label}
                        </button>
                      ))}
                    </div>
                  </>
                ) : (
                  <button type="button" className="site-navbar__drawer-link" onClick={() => navigate(item.path)}>
                    <span>{item.label}</span>
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </nav>
  );
}

export default Navbar;
